import { useState } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import SoftButton from './SoftButton';
import { playUiClick } from './humanityUiSounds';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * HumanityResult — final exhibition artifact / ticket screen.
 *
 * Visual order (per spec):
 *   1. Series label (THIS IS HUMANITY #0050)
 *   2. Title (sleek, mono, wide tracking — same typographic system as labels)
 *   3. Ticket caption (small, italic mono)
 *   4. 3:4 artwork frame (dominates)
 *   5. Email send section
 *   6. Small "1/1 artwork" + print notes
 *
 * Stages:
 *   - 'idle'    → image + form ready
 *   - 'sending' → submit button shows loading
 *   - 'sent'    → ritual ending copy + "Create another"
 *   - 'error'   → soft error copy + retry path
 */
export default function HumanityResult({ generation, sessionId, onCreateAnother, onSeeGallery }) {
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [phase, setPhase] = useState('idle'); // idle | sending | sent | error

  if (!generation || !generation.image_url) return null;

  const series = generation.formatted_series
    || (generation.series_number != null
      ? `#${String(generation.series_number).padStart(4, '0')}`
      : '');

  const handleSend = async (e) => {
    e?.preventDefault?.();
    playUiClick();
    const clean = email.trim();
    if (!clean || !EMAIL_RE.test(clean)) {
      setEmailError('Enter an email.');
      return;
    }
    setEmailError('');
    setPhase('sending');
    try {
      await axios.post(
        `${API}/humanity/session/${sessionId}/send-ticket`,
        { email: clean },
        { timeout: 15000 },
      );
      setPhase('sent');
    } catch (err) {
      console.warn('[HumanityMirror] send-ticket failed', err?.response?.data || err?.message);
      setPhase('error');
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 16, filter: 'blur(8px)' }}
      animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
      exit={{ opacity: 0 }}
      transition={{ duration: 1.1, ease: 'easeOut' }}
      className="humanity-result w-full max-w-md mx-auto text-center"
      data-testid="humanity-result"
    >
      {/* ─── Series + Title above image ───────────────────────── */}
      <div className="space-y-3 mb-6">
        <p
          className="font-mono tracking-[0.38em] uppercase text-[10px] sm:text-[11px] text-white/55"
          data-testid="result-series-label"
        >
          This Is Humanity {series}
        </p>
        {generation.title && (
          <h2
            className="humanity-result-title font-mono uppercase tracking-[0.22em] text-[14px] sm:text-[16px] text-white leading-snug px-3"
            data-testid="result-title"
          >
            {generation.title}
          </h2>
        )}
        {generation.ticket_caption && (
          <p
            className="font-mono italic text-[11px] sm:text-[12px] text-white/55 leading-relaxed px-6"
            data-testid="result-ticket-caption"
          >
            &ldquo;{generation.ticket_caption}&rdquo;
          </p>
        )}
      </div>

      {/* ─── 3:4 portrait artwork frame ───────────────────────── */}
      <div
        className="relative mx-auto w-full rounded-[14px] overflow-hidden border border-white/10 bg-black"
        style={{
          aspectRatio: '3 / 4',
          boxShadow: '0 0 60px rgba(144, 224, 239, 0.12), inset 0 0 20px rgba(255, 255, 255, 0.02)',
        }}
      >
        <img
          src={generation.image_url}
          alt={generation.title || 'Humanity fragment'}
          className="w-full h-full block"
          style={{ objectFit: 'cover' }}
          data-testid="result-image"
        />
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background: 'radial-gradient(ellipse at center, transparent 60%, rgba(0,0,0,0.45) 100%)',
          }}
        />
      </div>

      {/* Hairline divider */}
      <div className="h-px w-12 mx-auto bg-white/15 mt-7 mb-6" />

      {/* ─── Email capture / success ──────────────────────────── */}
      <AnimatePresence mode="wait">
        {phase === 'sent' ? (
          <motion.div
            key="sent"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.7 }}
            className="space-y-4"
            data-testid="ticket-sent"
          >
            <p className="font-mono tracking-[0.30em] uppercase text-[11px] sm:text-[12px] text-cyan-200/90">
              The artwork has been sent.
            </p>
            <p className="font-mono tracking-[0.04em] text-[12px] text-white/70 leading-relaxed max-w-xs mx-auto">
              Thank you for adding a fragment of humanity.
            </p>
            <p className="font-mono tracking-[0.04em] text-[11px] text-white/45 leading-relaxed max-w-xs mx-auto">
              You can print this 1/1 artwork at the event.
            </p>
            <div className="pt-3 flex items-center justify-center gap-3 flex-wrap">
              {onSeeGallery && (
                <SoftButton
                  variant="cyan"
                  size="sm"
                  onClick={() => { playUiClick(); onSeeGallery(); }}
                  testId="result-see-gallery-sent"
                >
                  See other fragments
                </SoftButton>
              )}
              <SoftButton
                variant="ghost"
                size="sm"
                onClick={() => { playUiClick(); onCreateAnother(); }}
                testId="create-another"
              >
                Create another one
              </SoftButton>
            </div>
          </motion.div>
        ) : (
          <motion.form
            key="form"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.7 }}
            onSubmit={handleSend}
            className="space-y-4"
            data-testid="ticket-form"
          >
            <p
              className="font-mono tracking-[0.32em] uppercase text-[10px] sm:text-[11px] text-white/55"
              data-testid="ticket-email-label"
            >
              Send your Humanity Fragment
            </p>
            <input
              type="email"
              autoComplete="email"
              spellCheck={false}
              value={email}
              onChange={(ev) => { setEmail(ev.target.value); if (emailError) setEmailError(''); }}
              placeholder="your@email.com"
              className="w-full bg-transparent border-b border-white/20 focus:border-cyan-200/60 outline-none text-center font-mono tracking-[0.04em] text-[13px] text-white placeholder:text-white/30 py-2 transition-colors"
              data-testid="ticket-email-input"
              disabled={phase === 'sending'}
            />
            {emailError && (
              <p className="font-mono tracking-[0.22em] uppercase text-[10px] text-rose-300/80" data-testid="ticket-email-error">
                {emailError}
              </p>
            )}
            {phase === 'error' && !emailError && (
              <p className="font-mono tracking-[0.22em] uppercase text-[10px] text-rose-300/80" data-testid="ticket-send-error">
                The artwork could not be sent.
              </p>
            )}
            <div className="pt-2">
              <SoftButton
                type="submit"
                variant="cyan"
                size="lg"
                disabled={phase === 'sending'}
                testId="ticket-send-button"
                className="humanity-cta-pulse humanity-interact-cta"
              >
                {phase === 'sending' ? 'Sending…' : 'Send'}
              </SoftButton>
            </div>
          </motion.form>
        )}
      </AnimatePresence>

      {/* ─── 1/1 + secondary actions beneath send area ─ */}
      {phase !== 'sent' && (
        <>
          <div className="mt-8 space-y-2 max-w-sm mx-auto px-2" data-testid="result-info-note">
            <p className="font-mono tracking-[0.04em] text-[11px] text-white/40 leading-relaxed">
              Each image is a unique 1/1 artwork based on the conversation you just had with the entity.
            </p>
          </div>
          <div className="mt-6 flex items-center justify-center gap-3 flex-wrap">
            {onSeeGallery && (
              <SoftButton
                variant="cyan"
                size="sm"
                onClick={() => { playUiClick(); onSeeGallery(); }}
                testId="result-see-gallery"
              >
                See other fragments
              </SoftButton>
            )}
            <SoftButton
              variant="ghost"
              size="sm"
              onClick={() => { playUiClick(); onCreateAnother(); }}
              testId="result-create-another"
            >
              Create another one
            </SoftButton>
          </div>
        </>
      )}
    </motion.div>
  );
}
